import { SAND_CATALOG } from '@/config/sandCatalog'

export const TYPE_NUM_TO_TAB = {
  '01': 'character',
  '02': 'animal',
  '03': 'plant',
  '04': 'artificial',
  '05': 'transport'
}

function parseJsonField (value, fallback) {
  if (value == null) return fallback
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    return value
  }
  if (typeof value === 'string' && value) {
    try {
      return JSON.parse(value)
    } catch (e) {
      return fallback
    }
  }
  return fallback
}

function pick (raw, camel, snake) {
  if (raw[camel] != null && raw[camel] !== '') return raw[camel]
  if (raw[snake] != null && raw[snake] !== '') return raw[snake]
  return undefined
}

/**
 * 统一解析 getSandTableData 返回体
 */
export function normalizeSandTableResponse (res) {
  const raw = res && res.data != null ? res.data : res
  if (!raw || typeof raw !== 'object') return null

  let code = pick(raw, 'code', 'code')
  let codeMeta = {}
  try {
    if (code) {
      codeMeta = typeof code === 'string' ? JSON.parse(code) : code
    }
  } catch (e) {
    codeMeta = {}
  }

  return {
    code: typeof code === 'string' ? code : JSON.stringify(codeMeta || {}),
    terrainData: parseJsonField(pick(raw, 'terrainData', 'terrain_data'), []),
    terrainWet: codeMeta.terrainWet || [],
    terrainMat: codeMeta.terrainMat || [],
    modeData: parseJsonField(pick(raw, 'modeData', 'mode_data'), []),
    allModelData: parseJsonField(
      pick(raw, 'allModelData', 'all_model_text'),
      []
    ),
    codingTime: pick(raw, 'codingTime', 'coding_time'),
    weather: codeMeta.weather,
    terrainMaterial: codeMeta.terrainMaterial,
    terrainVisible: codeMeta.terrainVisible,
    lakesRatio: pick(raw, 'lakesRatio', 'lakes_ratio'),
    mountainRatio: pick(raw, 'mountainRatio', 'mountain_ratio'),
    time: pick(raw, 'time', 'time'),
    slot: pick(raw, 'slot', 'slot'),
    name: pick(raw, 'name', 'name'),
    age: pick(raw, 'age', 'age'),
    sex: pick(raw, 'sex', 'sex'),
    reportName: pick(raw, 'reportName', 'report_name'),
    reportAge: pick(raw, 'reportAge', 'report_age'),
    reportSex: pick(raw, 'reportSex', 'report_sex'),
    reportEdc: pick(raw, 'reportEdc', 'report_education') || pick(raw, 'reportEdc', 'report_edc'),
    reportGrade: pick(raw, 'reportGrade', 'report_grade'),
    reportMarital: pick(raw, 'reportMarital', 'report_marriage'),
    userType: pick(raw, 'userType', 'user_type') || codeMeta.userType
  }
}

const EDU_OPTIONS = ['学前', '小学', '初中', '高中', '大专', '本科', '硕士', '博士']

/** 兼容旧版英文 value，统一成中文 */
const LEGACY_EDU_TO_LABEL = {
  high: '高中',
  college: '大专',
  bachelor: '本科',
  master: '硕士',
  doctor: '博士'
}

/** 学历原样使用中文，如「大专」 */
export function normalizeEducationLabel (value) {
  if (value == null || value === '') return ''
  const str = String(value).trim()
  if (LEGACY_EDU_TO_LABEL[str]) return LEGACY_EDU_TO_LABEL[str]
  if (EDU_OPTIONS.indexOf(str) !== -1) return str
  return str
}

/** 学历回显：使用 reportEdc，原样返回中文 */
export function resolveEducationFromBackend (reportEdc) {
  return normalizeEducationLabel(reportEdc)
}

const USER_TYPE_OPTIONS = ['事业', '情感']

const LEGACY_USER_TYPE_TO_LABEL = {
  1: '事业',
  2: '情感'
}

/** 类型原样使用中文，如「事业」；兼容后端 1/2 编码 */
export function normalizeUserTypeLabel (value) {
  if (value == null || value === '') return ''
  const str = String(value).trim()
  if (LEGACY_USER_TYPE_TO_LABEL[str]) return LEGACY_USER_TYPE_TO_LABEL[str]
  if (USER_TYPE_OPTIONS.indexOf(str) !== -1) return str
  return str
}

export function resolveUserTypeCode (value) {
  const label = normalizeUserTypeLabel(value)
  if (label === '情感') return '2'
  if (label === '事业') return '1'
  return '1'
}

/** 年满 18 周岁（含 18 岁）需填写事业/情感类型 */
export function isAdultSandUser (ageOrSession) {
  const raw =
    ageOrSession != null && typeof ageOrSession === 'object'
      ? ageOrSession.age
      : ageOrSession
  const age = parseInt(raw, 10)
  return !isNaN(age) && age >= 18
}

/** 从沙盘存档接口提取用户信息，供修改页回显 */
export function buildUserSessionFromSandResponse (res, slot) {
  const saved = normalizeSandTableResponse(res)
  if (!saved) return { slot, restoreSand: false }

  const sexLabel = saved.reportSex || saved.sex || ''
  const hasArchive = hasSavedScene(saved)
  const userType = normalizeUserTypeLabel(saved.userType)

  const age =
    saved.reportAge != null && saved.reportAge !== ''
      ? String(saved.reportAge)
      : saved.age != null
        ? String(saved.age)
        : ''

  const session = {
    slot: slot || saved.slot || '1',
    restoreSand: hasArchive,
    name: saved.reportName || saved.name || '',
    age,
    gender: sexLabel === '女' ? 'female' : 'male',
    education: resolveEducationFromBackend(saved.reportEdc),
    grade: saved.reportGrade || '',
    marital: saved.reportMarital || ''
  }
  if (userType && isAdultSandUser(age)) session.userType = userType
  return session
}

export function hasSavedScene (saved) {
  if (!saved) return false
  if (saved.modeData && saved.modeData.length > 0) return true
  if (saved.terrainData && saved.terrainData.length > 0) return true
  if (saved.terrainWet && saved.terrainWet.length > 0) return true
  if (saved.terrainMat && saved.terrainMat.length > 0) return true
  if (saved.code && saved.code !== '{}') {
    try {
      const meta = JSON.parse(saved.code)
      if (meta && (meta.weather || meta.terrainMaterial)) return true
      if (meta && ((meta.terrainWet && meta.terrainWet.length) || (meta.terrainMat && meta.terrainMat.length))) {
        return true
      }
    } catch (e) {
      return false
    }
  }
  return false
}

export function findSandCatalogItem (typeNum, name) {
  const tab = TYPE_NUM_TO_TAB[typeNum] || 'character'
  const list = SAND_CATALOG[tab] || []
  if (name) {
    const hit = list.find((item) => item.label === name)
    if (hit) return { tab, item: hit }
  }
  if (list.length) return { tab, item: list[0] }
  return null
}



// WEBPACK FOOTER //
// ./src/utils/sandTableRestore.js